import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Badge, Button, Input, Select } from '../components/Common';

function formatAction(action) {
  return action.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDetails(details) {
  if (!details) return '—';
  let obj = details;
  if (typeof obj === 'string') {
    try { obj = JSON.parse(obj); } catch { return details; }
  }

  const parts = [];

  if (obj.old_status && obj.new_status) {
    parts.push(`Status: ${obj.old_status} → ${obj.new_status}`);
  } else if (obj.old_status && obj.status) {
    parts.push(`Status: ${obj.old_status} → ${obj.status}`);
  }

  if (obj.groups && Array.isArray(obj.groups)) {
    parts.push(`Groups: ${obj.groups.join(', ')}`);
  }

  // Skip keys already handled above and metadata keys
  const skip = new Set(['old_status', 'new_status', 'status', 'groups', 'groupIds',
    'engagement_name', 'application_name', 'source']);
  const remaining = Object.entries(obj)
    .filter(([k, v]) => v !== null && v !== undefined && !skip.has(k));

  for (const [k, v] of remaining.slice(0, 4 - parts.length)) {
    const label = k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
    const val = Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v));
    parts.push(`${label}: ${val.length > 60 ? val.substring(0, 60) + '...' : val}`);
  }

  return parts.length > 0 ? parts.join(' | ') : '—';
}

const ACTION_COLORS = {
  LOGIN: '#3b82f6', LOGOUT: '#6b7280',
  CREATE: '#22c55e', UPDATE: '#f59e0b', DELETE: '#ef4444',
  RESET: '#8b5cf6', CHANGE: '#8b5cf6',
  ADD: '#22c55e', REMOVE: '#ef4444',
  UPLOAD: '#3b82f6', IMPORT: '#3b82f6'
};

function getActionColor(action) {
  for (const [prefix, color] of Object.entries(ACTION_COLORS)) {
    if (action.startsWith(prefix)) return color;
  }
  return '#6b7280';
}

// Inline SVG bar chart for events per day
function EventsBarChart({ data }) {
  if (!data || data.length === 0) return null;

  const sorted = [...data].sort((a, b) => new Date(a.day) - new Date(b.day));
  const maxCount = Math.max(...sorted.map(d => d.count), 1);
  const chartWidth = 700;
  const chartHeight = 180;
  const barPadding = 2;
  const barWidth = Math.max(4, Math.min(20, (chartWidth - 40) / sorted.length - barPadding));
  const totalBarsWidth = sorted.length * (barWidth + barPadding);
  const startX = 40;

  return (
    <Card style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Events Per Day</h3>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Last {sorted.length} days</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={Math.max(chartWidth, totalBarsWidth + 60)} height={chartHeight + 30} style={{ display: 'block' }}>
          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map(frac => {
            const y = chartHeight - frac * (chartHeight - 20);
            const val = Math.round(frac * maxCount);
            return (
              <g key={frac}>
                <text x={35} y={y + 4} textAnchor="end" fill="#6b7280" fontSize={10}>{val}</text>
                <line x1={startX} y1={y} x2={startX + totalBarsWidth} y2={y} stroke="#2d3748" strokeWidth={0.5} />
              </g>
            );
          })}
          {/* Bars */}
          {sorted.map((d, i) => {
            const barHeight = Math.max(1, (d.count / maxCount) * (chartHeight - 20));
            const x = startX + i * (barWidth + barPadding);
            const y = chartHeight - barHeight;
            return (
              <g key={d.day}>
                <rect
                  x={x} y={y} width={barWidth} height={barHeight}
                  fill="#3b82f6" rx={2} opacity={0.85}
                />
                <title>{new Date(d.day).toLocaleDateString()}: {d.count} events</title>
                {/* Show date label every ~5 bars or if few bars */}
                {(sorted.length <= 10 || i % Math.ceil(sorted.length / 10) === 0) && (
                  <text x={x + barWidth / 2} y={chartHeight + 14} textAnchor="middle" fill="#6b7280" fontSize={9}
                    transform={`rotate(-30, ${x + barWidth / 2}, ${chartHeight + 14})`}>
                    {new Date(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}

export default function ActivityLog() {
  const { apiFetch, user, hasGroup } = useAuth();
  // No client of their own means provider-side: Super Admins, and Engagement
  // Admins employed by the provider rather than by a client.
  const isProviderSide = hasGroup('Super Admins') || !user?.clientId;

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  // Dropdown data
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);

  // Provider-side staff choose whose activity to read; a client-side admin
  // always reads their own and is never offered the choice, because the server
  // would refuse it anyway.
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('');

  // Stats for chart
  const [stats, setStats] = useState(null);

  // Export dropdown
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);

  // Close export dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!isProviderSide) return;
    apiFetch('/clients').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setClients(d.filter(c => c.is_active !== false));
    }).catch(() => {});
  }, [apiFetch, isProviderSide]);

  const loadFilters = useCallback(async () => {
    try {
      const q = clientFilter ? `?clientId=${clientFilter}` : '';
      const [actRes, etRes, uRes, catRes, sevRes] = await Promise.all([
        apiFetch(`/audit/actions${q}`),
        apiFetch(`/audit/entity-types${q}`),
        apiFetch('/users'),
        apiFetch(`/audit/categories${q}`),
        apiFetch(`/audit/severities${q}`)
      ]);
      const list = async (res) => { const d = await res.json().catch(() => []); return Array.isArray(d) ? d : []; };
      setActions(await list(actRes));
      setEntityTypes(await list(etRes));
      setUsers(await list(uRes));
      setCategories(await list(catRes));
      setSeverities(await list(sevRes));
    } catch {}
  }, [apiFetch, clientFilter]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (clientFilter) params.set('clientId', clientFilter);
      const res = await apiFetch(`/audit/stats?${params.toString()}`);
      setStats(await res.json());
    } catch {}
  }, [apiFetch, startDate, endDate, clientFilter]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (actionFilter) params.set('action', actionFilter);
      if (entityTypeFilter) params.set('entityType', entityTypeFilter);
      if (userFilter) params.set('userId', userFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (search) params.set('search', search);
      if (categoryFilter) params.set('category', categoryFilter);
      if (severityFilter) params.set('severity', severityFilter);
      if (clientFilter) params.set('clientId', clientFilter);

      const res = await apiFetch(`/audit?${params.toString()}`);
      const data = await res.json().catch(() => null);
      setLogs(Array.isArray(data?.data) ? data.data : []);
      setTotal(data?.total || 0);
    } catch {}
    setLoading(false);
  }, [apiFetch, page, limit, actionFilter, entityTypeFilter, userFilter, startDate, endDate, search, categoryFilter, severityFilter, clientFilter]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const clearFilters = () => {
    setActionFilter(''); setEntityTypeFilter(''); setUserFilter('');
    setStartDate(''); setEndDate(''); setSearch('');
    setCategoryFilter(''); setSeverityFilter(''); setClientFilter(''); setPage(1);
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    try {
      const params = new URLSearchParams({ format });
      if (categoryFilter) params.set('category', categoryFilter);
      if (severityFilter) params.set('severity', severityFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (clientFilter) params.set('clientId', clientFilter);

      const res = await apiFetch(`/audit/export?${params.toString()}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const hasFilters = actionFilter || entityTypeFilter || userFilter || startDate || endDate || search || categoryFilter || severityFilter || clientFilter;
  const totalPages = Math.ceil(total / limit);

  const selectStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, outline: 'none'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Activity Log</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{total} total events</span>
          {/* Export Dropdown */}
          <div ref={exportRef} style={{ position: 'relative' }}>
            <Button variant="secondary" onClick={() => setShowExportMenu(!showExportMenu)} style={{ padding: '8px 16px' }}>
              Export
            </Button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
                background: 'var(--bg-card, #1a1f2e)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                minWidth: 140, overflow: 'hidden'
              }}>
                <button
                  onClick={() => handleExport('csv')}
                  style={{
                    display: 'block', width: '100%', padding: '10px 16px', border: 'none',
                    background: 'transparent', color: 'var(--text)', fontSize: 13, textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg-input)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  style={{
                    display: 'block', width: '100%', padding: '10px 16px', border: 'none',
                    background: 'transparent', color: 'var(--text)', fontSize: 13, textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg-input)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  Export as JSON
                </button>
              </div>
            )}
          </div>
          <Button variant="secondary" onClick={loadLogs} style={{ padding: '8px 16px' }}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Bar Chart */}
      {stats && stats.byDay && <EventsBarChart data={stats.byDay} />}

      {/* Filters */}
      <Card style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
          {isProviderSide && (
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Client</label>
              <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={selectStyle}>
                <option value="">All clients</option>
                {/* Events with no client are the provider's own: staff sign-ins,
                    client records, anything not tied to one tenant's data. */}
                <option value="provider">Provider only (no client)</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Search</label>
            <input
              placeholder="Search actions, users..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={selectStyle}
            />
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Action</label>
            <select
              value={actionFilter}
              onChange={e => { setActionFilter(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">All Actions</option>
              {actions.map(a => <option key={a} value={a}>{formatAction(a)}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Entity Type</label>
            <select
              value={entityTypeFilter}
              onChange={e => { setEntityTypeFilter(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">All Entity Types</option>
              {entityTypes.map(et => <option key={et} value={et}>{et.charAt(0).toUpperCase() + et.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Category</label>
            <select
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>Severity</label>
            <select
              value={severityFilter}
              onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">All Severities</option>
              {severities.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>User</label>
            <select
              value={userFilter}
              onChange={e => { setUserFilter(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">All Users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPage(1); }}
              style={selectStyle}
            />
          </div>
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPage(1); }}
              style={selectStyle}
            />
          </div>
        </div>
        {hasFilters && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={clearFilters} style={{ padding: '6px 14px', fontSize: 13 }}>
              Clear Filters
            </Button>
          </div>
        )}
      </Card>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-input)' }}>
              {[...(isProviderSide ? ['Timestamp', 'Client'] : ['Timestamp']),
                'User', 'Action', 'Entity', 'Details', 'IP Address'].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
                  textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em',
                  whiteSpace: 'nowrap'
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isProviderSide ? 7 : 6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={isProviderSide ? 7 : 6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No activity found</td></tr>
            ) : logs.map((log) => (
              <tr key={log.id} style={{ borderTop: '1px solid var(--border)' }}
                onMouseOver={e => e.currentTarget.style.background = 'var(--bg-input)'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '10px 16px', fontSize: 13, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                  {new Date(log.created_at).toLocaleDateString()}{' '}
                  <span style={{ fontWeight: 500, color: 'var(--text)' }}>{new Date(log.created_at).toLocaleTimeString()}</span>
                </td>
                {isProviderSide && (
                  <td style={{ padding: '10px 16px', fontSize: 13 }}>
                    {log.client_name || (
                      <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Provider</span>
                    )}
                  </td>
                )}
                <td style={{ padding: '10px 16px', fontSize: 13 }}>
                  <div style={{ fontWeight: 500 }}>{log.actor_label || log.full_name || log.username || 'System'}</div>
                  {log.full_name && log.username && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>@{log.username}</div>
                  )}
                </td>
                <td style={{ padding: '10px 16px', fontSize: 13 }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11,
                    fontWeight: 600, background: getActionColor(log.action) + '20', color: getActionColor(log.action),
                    border: `1px solid ${getActionColor(log.action)}40`
                  }}>
                    {formatAction(log.action)}
                  </span>
                </td>
                <td style={{ padding: '10px 16px', fontSize: 13 }}>
                  <div style={{ textTransform: 'capitalize' }}>{log.entity_type || '—'}</div>
                  {log.details?.engagement_name && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {log.details.engagement_name}
                      {log.details.application_name && ` · ${log.details.application_name}`}
                    </div>
                  )}
                  {!log.details?.engagement_name && log.details?.application_name && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{log.details.application_name}</div>
                  )}
                </td>
                <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)', maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatDetails(log.details)}
                </td>
                <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                  {log.ip_address || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total} entries
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}
              style={{ padding: '6px 14px', fontSize: 13 }}>
              Previous
            </Button>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '0 8px' }}>
              Page {page} of {totalPages}
            </span>
            <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              style={{ padding: '6px 14px', fontSize: 13 }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
